
import { faFileExcel, faFilePdf, faInfo } from "@elynx/free-solid-svg-icons";
import { Edit, PublishedWithChanges } from "@mui/icons-material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Avatar, Box, Button, createTheme, ListItem, ListItemAvatar, ListItemText, Stack, ThemeProvider, Typography, useTheme } from "@mui/material";
import { format } from "date-fns";
import React, { ReactNode, /*, useState*/ 
useMemo} from "react";
import { useTranslation } from "react-i18next";
import { ApplicationMimeTypesEnum } from "../../common/enums/ApplicationMimeTypesEnum";
import { INotificationMessageModel, NotificationMessagePriorityEnum, NotificationMessageTypeEnum } from "../../services/notification-message-service/Models";
import htmlParse from 'html-react-parser';
import { faFileSpreadsheet } from "@elynx/pro-regular-svg-icons";
import { LiveDataCommandEnum } from "../../services/live-data-service/Models";
import { useAppSettings } from "../../contexts/app-context/AppContext";

export enum UserMessageActionEnum {
    Download = 1,
    Delete = 2,
    Display = 3,
    Acknowledge = 4
}

export type UserMessageListItemProps = {
    key?: React.Key | null | undefined
    item: INotificationMessageModel,
    onAction: (action: UserMessageActionEnum, payload: INotificationMessageModel) => void,
    expanded: boolean;
};

export function UserMessageListItem({
    key,
    item,
    onAction
}: UserMessageListItemProps) {
    const [appSettings] = useAppSettings();
    const theme = useTheme();
    const { t } = useTranslation();
    const mainTheme = useMemo(
        () => {
            return createTheme(appSettings.themeSet[appSettings.themeSet.mode].main);
        },
        [appSettings.themeSet]
    );


    function getTitle(): ReactNode {
        return (
            item.messageType === NotificationMessageTypeEnum.UserMessage ?
                    htmlParse(item.description)
                :
            <Box sx={{fontWeight: 600}}>
                {item.description}
            </Box>
        );
    }

    function getBody(): ReactNode {
        return (
            <>
                {item.messageType === NotificationMessageTypeEnum.LiveDataStatusMessage &&

                    <Typography
                        component="span"
                        variant="body2"
                        sx={{
                            color: "text.primary",
                            display: 'flex',
                            flexDirection: 'column',
                            flex: '0 0 auto'
                        }}>
                        {
                        `${t(item.command === LiveDataCommandEnum.DemandScan ? 'livedata.demandscan' : 'livedata.writedown')} ${item.success ? ' was successful' : 'failed' }`
                        }
                    </Typography>

                }
                {item.messageType !== NotificationMessageTypeEnum.UserMessage &&
                    <>
                        <Typography
                            component="span"
                            variant="body2"
                            sx={{
                                color: "text.primary",
                                display: 'flex',
                                flexDirection: 'column',
                                flex: '0 0 auto'
                            }}>
                                {
                                `${item.messageType === NotificationMessageTypeEnum.ExportMessage ?
                                        'Exported' :
                                    item.messageType === NotificationMessageTypeEnum.LiveDataStatusMessage ?
                                        'Completed' :
                                        'Created'} : ${format(item.messageDate as Date, "P pp")}`
                                }
                        </Typography>

                        <Typography
                            component="span"
                            variant="body2"
                            sx={{
                                color: "text.primary",
                                display: 'flex',
                                flexDirection: 'column',
                                flex: '0 0 auto'
                            }}>
                                {`Expires: ${format(item.messageExpirationDate as Date, "P pp")}`}
                        </Typography>
                    </>
                }
                <Stack component='span' direction='row' sx={{justifyContent: 'flex-end'}}>
                <ThemeProvider theme={mainTheme}>
                    {
                        item.messageType === NotificationMessageTypeEnum.ExportMessage &&
                        <Button size="small" onClick={() => onAction(UserMessageActionEnum.Download, item)}>
                            {t('buttons.download')}
                        </Button>
                    }

                    {
                        (item.messageType === NotificationMessageTypeEnum.QuickTrendJSON ||
                        item.messageType === NotificationMessageTypeEnum.TrendingJSON ||
                        item.messageType === NotificationMessageTypeEnum.ReportingJSON ||
                        item.messageType === NotificationMessageTypeEnum.DataFileImportLog ||
                        (item.messageType === NotificationMessageTypeEnum.LiveDataStatusMessage && !item.success)) &&
                        <Button size="small" onClick={() => onAction(UserMessageActionEnum.Display, item)}>
                            {t('buttons.view')}
                        </Button>
                    }

                    {
                        item.messageType !== NotificationMessageTypeEnum.UserMessage &&
                        <Button size="small" onClick={() => onAction(UserMessageActionEnum.Delete, item)}>
                            {t('buttons.delete')}
                        </Button>
                    }

                    {
                        item.messageType === NotificationMessageTypeEnum.UserMessage &&
                        item.acknowledged === null &&
                        <Button size="small" onClick={() => onAction(UserMessageActionEnum.Acknowledge, item)}>
                            {t('buttons.okay')}
                        </Button>
                    }
                </ThemeProvider>                                        
                </Stack>
            </>
        );
    }

    return (

        <ListItem key={key} alignItems="flex-start">
            <ListItemAvatar>
                <Avatar sx={{
                        backgroundColor: item.priority === NotificationMessagePriorityEnum.High ||
                                            (item.messageType === NotificationMessageTypeEnum.LiveDataStatusMessage &&
                                                !item.success) ?
                                        theme.palette.error.main :
                                        undefined
                        }}>
                    {
                        item.messageType === NotificationMessageTypeEnum.UserMessage ?
                        <FontAwesomeIcon
                            icon={faInfo}
                            size={'1x'}
                        /> :
                        item.messageType === NotificationMessageTypeEnum.LiveDataStatusMessage ?
                            item.command === LiveDataCommandEnum.DemandScan ?
                                <PublishedWithChanges /> :
                                <Edit />
                        :
                        item.messageType === NotificationMessageTypeEnum.QuickTrendJSON ||
                                item.messageType === NotificationMessageTypeEnum.ReportingJSON ?
                            <FontAwesomeIcon
                            icon={faFileSpreadsheet}
                            size={'1x'}
                            /> :
                        item.mimeType === ApplicationMimeTypesEnum.MSExcel ?
                            <FontAwesomeIcon
                                icon={faFileExcel}
                                size={'1x'}
                                />
                            :
                            <FontAwesomeIcon
                                icon={faFilePdf}
                                size={'1x'}
                            />
                    }
                </Avatar>
            </ListItemAvatar>
            <ListItemText
                primary={getTitle()}
                secondary={getBody()}
                />
        </ListItem>
    );
}
